import React, { useEffect, useMemo, useRef, useState } from "react";
import { Mafs, Theme, Point, Line, Circle, Text } from "mafs";

/**
 * GeometricConstruction (feedback visualisation)
 *
 * Aligns with GeometricConstructionConfig + GeometricConstructionValidator:
 * - Step types: POINT | SEGMENT | LINE | ARC
 * - ARC (server canonical) is rendered as a circle (full arc for demo feedback)
 * - snapToGrid=true snaps to INTEGER grid (banker's rounding, like Math.rint)
 * - Coordinates and offsets clamped to [-20, 20]
 * - Demo reliability: never blank; has default perpendicular bisector example
 *
 * Config (GeometricConstructionConfig.java):
 * {
 *   allowedTools?: ["RULER","COMPASS","PROTRACTOR",...],
 *   targetGoalType?: "ANGLE_BISECTOR" | "PERPENDICULAR_BISECTOR" | "CONSTRUCT_PERPENDICULAR_LINE" | ...,
 *   snapToGrid?: boolean,
 *   anchors: [{ id:"A", x:-3, y:0, label:"A", draggable:true }],
 *   steps: [
 *     { type:"SEGMENT", p1:{anchorId:"A"}, p2:{anchorId:"B"}, caption:"Draw AB" },
 *     { type:"ARC", center:{anchorId:"A"}, radius:6, caption:"Arc from A" }
 *   ]
 * }
 *
 * Notes:
 * - Legacy compatibility: accepts step type "CIRCLE" as alias for "ARC".
 * - Legacy compatibility: will read cfg.targetGoalText if present, but config/validator no longer use it.
 */
export const GeometricConstruction = ({ config }) => {
  const safeNum = (n, fallback) => (Number.isFinite(n) ? n : fallback);
  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

  // Banker's rounding (ties to even) to match Java Math.rint for integers
  const rint = (x) => {
    if (!Number.isFinite(x)) return 0;
    const ax = Math.abs(x);
    const floor = Math.floor(ax);
    const frac = ax - floor;

    let rounded;
    if (frac < 0.5) rounded = floor;
    else if (frac > 0.5) rounded = floor + 1;
    else {
      // exactly .5 -> tie to even
      rounded = floor % 2 === 0 ? floor : floor + 1;
    }
    return x < 0 ? -rounded : rounded;
  };

  const cfg = config || {};

  // Validator bounds
  const MIN_COORD = -20;
  const MAX_COORD = 20;

  const snapToGrid = cfg.snapToGrid !== false;

  const rawAnchors = Array.isArray(cfg.anchors) ? cfg.anchors : [];
  const rawSteps = Array.isArray(cfg.steps) ? cfg.steps : [];

  const defaulted = useMemo(() => {
    // “Demo-safe” default: perpendicular bisector of AB.
    const A = { id: "A", x: -3, y: 0, label: "A", draggable: true };
    const B = { id: "B", x: 3, y: 0, label: "B", draggable: true };

    const dist = (p, q) => Math.hypot(p.x - q.x, p.y - q.y);
    const r = dist(A, B);

    const mid = { x: (A.x + B.x) / 2, y: (A.y + B.y) / 2 };
    const half = r / 2;
    const h = Math.sqrt(Math.max(0, r * r - half * half));
    const C = { x: mid.x, y: mid.y + h };
    const D = { x: mid.x, y: mid.y - h };

    const def = {
      anchors: [A, B],
      steps: [
        { type: "SEGMENT", p1: { anchorId: "A" }, p2: { anchorId: "B" }, label: "AB", caption: "Draw the segment AB.", keyStep: true },
        { type: "ARC", center: { anchorId: "A" }, radius: r, caption: "From A, draw an arc with radius AB." },
        { type: "ARC", center: { anchorId: "B" }, radius: r, caption: "From B, draw an arc with radius AB." },
        { type: "POINT", p1: { x: C.x, y: C.y }, label: "C", caption: "Mark the first intersection point C." },
        { type: "POINT", p1: { x: D.x, y: D.y }, label: "D", caption: "Mark the second intersection point D." },
        { type: "LINE", p1: { x: C.x, y: C.y }, p2: { x: D.x, y: D.y }, label: "CD", caption: "Draw line CD (this is the perpendicular bisector).", keyStep: true },
        { type: "POINT", p1: { x: mid.x, y: mid.y }, label: "M", caption: "The midpoint M lies where CD crosses AB.", keyStep: true },
      ],
      targetGoalType: "PERPENDICULAR_BISECTOR",
      // legacy only (config no longer includes it)
      targetGoalText: "Construct the perpendicular bisector of AB.",
    };

    const anchors = rawAnchors.length > 0 ? rawAnchors : def.anchors;
    const steps = rawSteps.length > 0 ? rawSteps : def.steps;

    // Config no longer includes goalText; keep legacy read if it appears.
    const goalText = (cfg.targetGoalText ?? def.targetGoalText ?? "").toString();
    const goalType = (cfg.targetGoalType ?? def.targetGoalType ?? "OTHER").toString();

    return { anchors, steps, goalText, goalType };
  }, [cfg.targetGoalText, cfg.targetGoalType, rawAnchors, rawSteps]);

  // --- Anchor state (supports Reset + dragging) ---
  const sanitizeAnchor = (a, idx) => {
    const rawId = (a?.id ?? "").toString().trim();
    const id = rawId || String.fromCharCode("A".charCodeAt(0) + (idx % 26));

    const x0 = safeNum(a?.x, 0);
    const y0 = safeNum(a?.y, 0);
    const x = clamp(snapToGrid ? rint(x0) : x0, MIN_COORD, MAX_COORD);
    const y = clamp(snapToGrid ? rint(y0) : y0, MIN_COORD, MAX_COORD);

    const label = (a?.label ?? id).toString();
    const draggable = a?.draggable !== false;

    return { id, x, y, label, draggable };
  };

  const initialAnchors = useMemo(() => {
    const out = [];
    const seen = new Set();
    let idx = 0;

    for (const a of defaulted.anchors) {
      const s = sanitizeAnchor(a, idx++);
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      out.push(s);
      if (out.length >= 8) break;
    }

    if (out.length === 0) {
      out.push(sanitizeAnchor({ id: "A", x: -3, y: 0, label: "A", draggable: true }, 0));
      out.push(sanitizeAnchor({ id: "B", x: 3, y: 0, label: "B", draggable: true }, 1));
    }

    return out;
  }, [defaulted.anchors, snapToGrid]);

  const [anchors, setAnchors] = useState(initialAnchors);

  // Default to “completed view” (best for feedback), but keep playback obvious.
  const [stepIndex, setStepIndex] = useState(Math.max(0, defaulted.steps.length - 1));
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    setAnchors(initialAnchors);
    setStepIndex(Math.max(0, (defaulted.steps?.length || 1) - 1));
    setPlaying(false);
  }, [initialAnchors, defaulted.steps]);

  // --- Playback timer ---
  useEffect(() => {
    if (!playing) return undefined;
    const t = window.setInterval(() => {
      setStepIndex((i) => {
        const max = Math.max(0, defaulted.steps.length - 1);
        if (i >= max) return max;
        return i + 1;
      });
    }, 800);
    return () => window.clearInterval(t);
  }, [playing, defaulted.steps.length]);

  useEffect(() => {
    const max = Math.max(0, defaulted.steps.length - 1);
    if (playing && stepIndex >= max) setPlaying(false);
  }, [playing, stepIndex, defaulted.steps.length]);

  // --- Point resolution ---
  const anchorMap = useMemo(() => {
    const m = new Map();
    for (const a of anchors) m.set(a.id, a);
    return m;
  }, [anchors]);

  const sanitizeOffset = (v) => {
    const n = safeNum(v, 0);
    return clamp(n, MIN_COORD, MAX_COORD);
  };

  const resolvePoint = (p) => {
    if (!p) return null;

    // Legacy array: [x, y]
    if (Array.isArray(p) && p.length >= 2) {
      const x = safeNum(p[0], null);
      const y = safeNum(p[1], null);
      if (x == null || y == null) return null;
      return [clamp(x, MIN_COORD, MAX_COORD), clamp(y, MIN_COORD, MAX_COORD)];
    }

    const anchorId = (p.anchorId ?? "").toString().trim();
    if (anchorId) {
      const a = anchorMap.get(anchorId);
      if (!a) return null;
      const ox = sanitizeOffset(p.offsetX);
      const oy = sanitizeOffset(p.offsetY);
      return [clamp(a.x + ox, MIN_COORD, MAX_COORD), clamp(a.y + oy, MIN_COORD, MAX_COORD)];
    }

    const x = safeNum(p.x, null);
    const y = safeNum(p.y, null);
    if (x == null || y == null) return null;

    return [clamp(x, MIN_COORD, MAX_COORD), clamp(y, MIN_COORD, MAX_COORD)];
  };

  const steps = useMemo(() => {
    // Minimal sanitize: coerce types, drop unknown types, keep captions/labels strings
    const out = [];
    for (const s of defaulted.steps) {
      let type = (s?.type ?? "").toString().trim().toUpperCase();
      if (!type) continue;

      // Legacy alias: CIRCLE -> ARC
      if (type === "CIRCLE") type = "ARC";

      // Only supported step types from validator
      if (!["POINT", "SEGMENT", "LINE", "ARC"].includes(type)) continue;

      out.push({
        ...s,
        type,
        caption: (s?.caption ?? "").toString(),
        label: (s?.label ?? "").toString(),
        keyStep: !!s?.keyStep,
      });

      if (out.length >= 60) break;
    }
    return out;
  }, [defaulted.steps]);

  const maxStep = Math.max(0, steps.length - 1);
  const currentStep = clamp(stepIndex, 0, maxStep);

  // --- Dynamic viewBox (anchors + any explicit points) ---
  const viewBox = useMemo(() => {
    const xs = [];
    const ys = [];

    for (const a of anchors) {
      xs.push(a.x);
      ys.push(a.y);
    }
    for (const s of steps) {
      const p1 = resolvePoint(s.p1);
      const p2 = resolvePoint(s.p2);
      const c = resolvePoint(s.center);
      if (p1) { xs.push(p1[0]); ys.push(p1[1]); }
      if (p2) { xs.push(p2[0]); ys.push(p2[1]); }
      if (c) { xs.push(c[0]); ys.push(c[1]); }
    }

    if (xs.length === 0 || ys.length === 0) return { x: [-10, 10], y: [-10, 10] };

    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const spanX = Math.max(6, maxX - minX);
    const spanY = Math.max(6, maxY - minY);
    const pad = Math.max(2, 0.25 * Math.max(spanX, spanY));

    return {
      x: [clamp(minX - pad, -60, 60), clamp(maxX + pad, -60, 60)],
      y: [clamp(minY - pad, -60, 60), clamp(maxY + pad, -60, 60)],
    };
  }, [anchors, steps]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Dragging anchors (overlay hit-test in pixel space) ---
  const stageRef = useRef(null);
  const dragRef = useRef({ dragging: false, anchorId: null });

  const toPixel = (x, y, rect) => {
    const [xMin, xMax] = viewBox.x;
    const [yMin, yMax] = viewBox.y;
    const px = ((x - xMin) / (xMax - xMin)) * rect.width;
    const py = ((yMax - y) / (yMax - yMin)) * rect.height;
    return [px, py];
  };

  const toMath = (px, py, rect) => {
    const [xMin, xMax] = viewBox.x;
    const [yMin, yMax] = viewBox.y;
    const x = xMin + (px / rect.width) * (xMax - xMin);
    const y = yMax - (py / rect.height) * (yMax - yMin);
    return [x, y];
  };

  const onPointerDown = (e) => {
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect) return;

    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;

    // Find nearest draggable anchor
    let best = null;
    for (const a of anchors) {
      if (!a.draggable) continue;
      const [ax, ay] = toPixel(a.x, a.y, rect);
      const d2 = (ax - px) ** 2 + (ay - py) ** 2;
      if (best == null || d2 < best.d2) best = { id: a.id, d2 };
    }

    if (best && best.d2 <= 18 * 18) {
      dragRef.current = { dragging: true, anchorId: best.id };
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }
  };

  const onPointerMove = (e) => {
    if (!dragRef.current.dragging) return;
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect) return;

    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    let [x, y] = toMath(px, py, rect);

    // Match validator: integer snap + clamp [-20,20]
    if (snapToGrid) {
      x = rint(x);
      y = rint(y);
    }

    x = clamp(x, MIN_COORD, MAX_COORD);
    y = clamp(y, MIN_COORD, MAX_COORD);

    const id = dragRef.current.anchorId;
    setAnchors((prev) => prev.map((a) => (a.id === id ? { ...a, x, y } : a)));
  };

  const onPointerUp = (e) => {
    if (!dragRef.current.dragging) return;
    dragRef.current = { dragging: false, anchorId: null };
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  // --- Rendering helpers ---
  const drawLineAsSegment = (p1, p2) => {
    const [xMin, xMax] = viewBox.x;
    const [yMin, yMax] = viewBox.y;

    const x1 = p1[0], y1 = p1[1];
    const x2 = p2[0], y2 = p2[1];

    const dx = x2 - x1;
    const dy = y2 - y1;
    const mag2 = dx * dx + dy * dy;

    if (mag2 < 1e-10) return null;

    const span = Math.max(xMax - xMin, yMax - yMin);
    const t = span * 2;

    return [
      [x1 - dx * t, y1 - dy * t],
      [x1 + dx * t, y1 + dy * t],
    ];
  };

  // If validator default gave a degenerate LINE (same p1/p2), try to make it meaningful for demos:
  const autoPerpBisectorLine = () => {
    if (anchors.length < 2) return null;
    const A = anchors[0];
    const B = anchors[1];
    const mx = (A.x + B.x) / 2;
    const my = (A.y + B.y) / 2;

    const vx = B.x - A.x;
    const vy = B.y - A.y;
    const len = Math.hypot(vx, vy);
    if (!Number.isFinite(len) || len < 1e-6) return null;

    // Perpendicular direction
    const px = -vy / len;
    const py = vx / len;

    const span = Math.max(viewBox.x[1] - viewBox.x[0], viewBox.y[1] - viewBox.y[0]) * 2;
    return [
      [mx - px * span, my - py * span],
      [mx + px * span, my + py * span],
    ];
  };

  const visibleSteps = steps.slice(0, currentStep + 1);

  // --- UI actions ---
  const resetAll = () => {
    setAnchors(initialAnchors);
    setStepIndex(Math.max(0, steps.length - 1));
    setPlaying(false);
  };

  const stepBack = () => {
    setPlaying(false);
    setStepIndex((i) => Math.max(0, i - 1));
  };

  const stepForward = () => {
    setPlaying(false);
    setStepIndex((i) => Math.min(maxStep, i + 1));
  };

  const togglePlay = () => {
    if (currentStep >= maxStep) {
      setStepIndex(0);
      setPlaying(true);
      return;
    }
    setPlaying((p) => !p);
  };

  const goalType = (defaulted.goalType ?? "OTHER").toString().trim();
  const legacyGoalText = (defaulted.goalText ?? "").toString().trim(); // legacy only
  const showGoal = !!goalType || !!legacyGoalText;

  return (
      <div className="ch-geoconstruct-widget">
        <div className="ch-geoconstruct-header">
          <div className="ch-geoconstruct-title-row">
            <h3 className="ch-geoconstruct-title">Geometric Construction</h3>
            {goalType && <span className="ch-geoconstruct-pill">{goalType.replaceAll("_", " ")}</span>}
          </div>

          <div className="ch-geoconstruct-header-actions">
            <button type="button" className="ch-btn-outline" onClick={resetAll} title="Reset">
              Reset
            </button>
          </div>
        </div>

        {showGoal && (
            <div className="ch-geoconstruct-goal">
              <span className="ch-geoconstruct-strong">Goal:</span>{" "}
              {legacyGoalText || goalType.replaceAll("_", " ")}
            </div>
        )}

        <div
            className="ch-geoconstruct-stage"
            ref={stageRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
        >
          <Mafs viewBox={viewBox} pan={false} zoom={false}>
            {/* Draw steps (previous dim, current highlight) */}
            {visibleSteps.map((s, i) => {
              const isCurrent = i === currentStep;
              const isKey = !!s.keyStep;
              const opacity = isCurrent ? 1 : isKey ? 0.55 : 0.25;
              const color = isCurrent ? Theme.indigo : Theme.gray;

              const p1 = resolvePoint(s.p1);
              const p2 = resolvePoint(s.p2);
              const c = resolvePoint(s.center);

              // Radius is allowed for ARC; validate like the server
              const rRaw = s.radius;
              const r = rRaw == null ? null : safeNum(rRaw, null);
              const rOk = r != null && Number.isFinite(r) && r > 0 ? clamp(r, 0.0001, 200) : null;

              switch (s.type) {
                case "POINT": {
                  if (!p1) return null;
                  return (
                      <g key={`s-${i}`} opacity={opacity}>
                        <Point x={p1[0]} y={p1[1]} color={color} />
                        {s.label && (
                            <Text x={p1[0] + 0.25} y={p1[1] + 0.25} size={16} color={color}>
                              {s.label}
                            </Text>
                        )}
                      </g>
                  );
                }

                case "SEGMENT": {
                  if (!p1 || !p2) return null;
                  return (
                      <Line.Segment
                          key={`s-${i}`}
                          point1={p1}
                          point2={p2}
                          color={color}
                          weight={isCurrent ? 3 : 2}
                          opacity={opacity}
                      />
                  );
                }

                case "LINE": {
                  // If validator default gave a degenerate line, auto-generate a perpendicular bisector line for demo stability.
                  let seg = null;
                  if (p1 && p2) seg = drawLineAsSegment(p1, p2);

                  if (!seg && goalType === "PERPENDICULAR_BISECTOR") {
                    seg = autoPerpBisectorLine();
                  }

                  if (!seg) return null;

                  return (
                      <Line.Segment
                          key={`s-${i}`}
                          point1={seg[0]}
                          point2={seg[1]}
                          color={color}
                          weight={isCurrent ? 3 : 2}
                          opacity={opacity}
                      />
                  );
                }

                case "ARC": {
                  if (!c) return null;

                  // Prefer explicit radius; else compute from center->p1 (matches validator rule: radius OR point-on-arc)
                  let rad = rOk;
                  if (rad == null && p1) {
                    const computed = Math.hypot(p1[0] - c[0], p1[1] - c[1]);
                    rad = Number.isFinite(computed) && computed > 0 ? clamp(computed, 0.0001, 200) : null;
                  }
                  if (rad == null) return null;

                  return (
                      <Circle
                          key={`s-${i}`}
                          center={c}
                          radius={rad}
                          color={color}
                          opacity={opacity}
                      />
                  );
                }

                default:
                  return null;
              }
            })}

            {/* Anchors (always visible and draggable) */}
            {anchors.map((a) => (
                <g key={`a-${a.id}`}>
                  <Point x={a.x} y={a.y} color={Theme.orange} />
                  <Text x={a.x + 0.25} y={a.y + 0.25} size={16} color={Theme.orange}>
                    {a.label}
                  </Text>
                </g>
            ))}
          </Mafs>

          <div className="ch-geoconstruct-hint">
            {snapToGrid ? "Drag anchors (snaps to integer grid)" : "Drag anchors"} · Use the step controls below
          </div>
        </div>

        <div className="ch-feedback-panel ch-geoconstruct-controls">
        <div className="ch-feedback-panel-header">
          <div className="ch-feedback-panel-title-row">
            <div className="ch-feedback-panel-title">Controls</div>
            <span className="ch-feedback-pill">Step {currentStep + 1}/{maxStep + 1}</span>
          </div>
        </div>
        <div className="ch-feedback-panel-body">
<div className="ch-geoconstruct-control-row">
            <button type="button" className="ch-btn-outline" onClick={stepBack} disabled={currentStep <= 0}>
              ◀ Step
            </button>

            <button type="button" className="ch-btn" onClick={togglePlay}>
              {playing ? "Pause" : currentStep >= maxStep ? "Replay" : "Play"}
            </button>

            <button type="button" className="ch-btn-outline" onClick={stepForward} disabled={currentStep >= maxStep}>
              Next ▶
            </button>
        </div>
      </div>

          <div className="ch-geoconstruct-scrubber">
            <input
                type="range"
                min={0}
                max={maxStep}
                step={1}
                value={currentStep}
                onChange={(e) => {
                  setPlaying(false);
                  setStepIndex(parseInt(e.target.value, 10));
                }}
                className="ch-geoconstruct-range"
                aria-label="Construction step"
            />
            <span className="ch-geoconstruct-stepbadge">
            Step {currentStep + 1}/{maxStep + 1}
          </span>
          </div>
        </div>

        {/* Explainability overlay */}
        <div className="ch-geoconstruct-caption">
          <span className="ch-geoconstruct-strong">Why this step:</span>{" "}
          {steps[currentStep]?.caption || "—"}
        </div>

        <div className="ch-feedback-panel ch-geoconstruct-legend-panel">
        <div className="ch-feedback-panel-header">
          <div className="ch-feedback-panel-title-row">
            <div className="ch-feedback-panel-title">Legend</div>
          </div>
        </div>
        <div className="ch-feedback-panel-body">
<span className="ch-geoconstruct-legend-item">
          <span className="ch-geoconstruct-swatch" style={{ background: Theme.indigo }} /> Current step
        </span>
          <span className="ch-geoconstruct-legend-item">
          <span className="ch-geoconstruct-swatch" style={{ background: Theme.gray }} /> Previous steps
        </span>
          <span className="ch-geoconstruct-legend-item">
          <span className="ch-geoconstruct-swatch" style={{ background: Theme.orange }} /> Anchor points
        </span>
        </div>
      </div>

        {/* Step list */}
        <div className="ch-feedback-panel ch-geoconstruct-steps-panel">
        <div className="ch-feedback-panel-header">
          <div className="ch-feedback-panel-title-row">
            <div className="ch-feedback-panel-title">Steps</div>
            <span className="ch-feedback-pill">{steps.length} total</span>
          </div>
        </div>
        <div className="ch-feedback-panel-body">
          <div className="ch-geoconstruct-steplist">
          {steps.map((s, i) => {
            const active = i === currentStep;
            const text = s.caption || s.label || `${s.type}`;
            return (
                <button
                    key={`li-${i}`}
                    type="button"
                    className={`ch-geoconstruct-step ${active ? "ch-is-active" : ""}`}
                    onClick={() => {
                      setPlaying(false);
                      setStepIndex(i);
                    }}
                >
                  <span className="ch-geoconstruct-stepnum">{i + 1}</span>
                  <span className="ch-geoconstruct-steptext">{text}</span>
                  {s.keyStep && <span className="ch-geoconstruct-key">Key</span>}
                </button>
            );
          })}
        </div>
        </div>
      </div>
      </div>
  );
};
